import React from "react";
import OnboardingLayout from "./OnboardingLayout";
import { HiArrowRight, HiOutlineSparkles } from "react-icons/hi";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import { ReactComponent as GlobeIcon } from "../../assets/icons/globe-03.svg";
import { ReactComponent as CompassIcon } from "../../assets/icons/compass-03.svg";


/** Step in the onboarding flow that summarizes user inputs and provides recommendations. */
// Define the props for the StepSummary component
interface StepSummaryProps {
  role: string;
  focus: string;
  recommendations: string[];
  selectedNeeds: string[];
  regions: string[];
  markets: string[];
  description?: string;
  hasExistingPersonas?: boolean;
  onClarify?: () => void;
  onConfirm?: () => void;
  onBack?: () => void;
  onExit?: () => void;
}

// Main component function
function StepSummary({
  role,
  focus,
  recommendations,
  selectedNeeds,
  regions,
  markets,
  description,
  hasExistingPersonas,
  onClarify,
  onConfirm,
  onBack,
  onExit,
}: StepSummaryProps) {
  // Separate work areas and assets from selectedNeeds
  const WORK_AREAS = ["Trading", "Operations", "Asset Management", "Market Analysis", "Risk", "Other"];
  const ASSETS = ["Hydro", "Wind", "Solar", "Battery", "Thermal", "Load"];
  
  const workAreas = selectedNeeds.filter(item => WORK_AREAS.includes(item));
  const assets = selectedNeeds.filter(item => ASSETS.includes(item));
  
  const objectiveLabel = description || "Not specified";

  return (
    <OnboardingLayout 
      showProgress 
      progressValue={0.8}
      hasExistingPersonas={hasExistingPersonas}
      onExit={onExit}
    >
      {/* Main content area */}
      <div className="px-8 pb-10 pt-6 space-y-8">
        {/* Header section with icon and text */}
        <div className="flex items-start gap-4">
          <div className="rounded-3xl bg-[rgba(162,187,191,0.1)] p-2">
            <HiOutlineSparkles className="text-eai-primary-500" size={24} />
          </div>
          <div>
            <p className="text-base font-semibold text-eai-dark">Got it!</p>
            <p className="text-sm text-gray-500">
              Here's what I think you need based on your answers.
            </p>
          </div>
        </div>
        {/* Summary sections */}
        <div className="space-y-4">
          {/* What you do section with tags */}
          <div className="bg-[rgba(162,187,191,0.15)] rounded-xl px-4 py-4">
            <div className="flex items-start gap-3">
              <div>
                <BriefcaseIcon className="w-5 h-5 text-eai-primary-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">What you do</p>
                {workAreas.length > 0 || assets.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {workAreas.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-green-50 rounded-full text-sm font-medium text-green-700 border border-green-200"
                      >
                        {tag}
                      </span>
                    ))}
                    {assets.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-blue-50 rounded-full text-sm font-medium text-blue-700 border border-blue-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-base text-gray-400">Not specified</p>
                )}
              </div>
            </div>
          </div>

          {/* Where you're focused section */}
          <div className="bg-[rgba(63,131,248,0.1)] rounded-xl px-4 py-4">
            <div className="flex items-start gap-3">
              <div>
                <GlobeIcon className="w-5 h-5 text-[#3f83f8]" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">Where you're focused</p>
                {regions.length > 0 || markets.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {regions.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-orange-50 rounded-full text-sm font-medium text-orange-700 border border-orange-200"
                      >
                        {tag}
                      </span>
                    ))}
                    {markets.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-primary-50 rounded-full text-sm font-medium text-primary-700 border border-primary-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-base text-gray-400">Any location</p>
                )}
              </div>
            </div>
          </div>

          {/* Your objective section */}
          <div className="bg-[#f8fbea] rounded-xl px-4 py-4">
            <div className="flex items-start gap-3">
              <div>
                <CompassIcon className="w-5 h-5 text-eai-primary-accent" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">Your objective</p>
                <p className="text-base font-medium text-eai-dark">
                  {objectiveLabel}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClarify ?? onBack}
            className="rounded-lg border border-eai-bg-lt2 px-4 py-2 text-sm text-eai-dark hover:border-eai-primary-500"
          >
            Let me clarify...
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center gap-2 rounded-lg bg-eai-primary-accent px-6 py-2 text-sm font-medium text-white transition hover:opacity-90"
          >
            Yes, perfect
            <HiArrowRight />
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}

export default StepSummary;
